// src/pages/dms/GJMS.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Breadcrumbs,
    Tabs, // Import Tabs
    Tab, // Import Tab
    Paper,
    useMediaQuery,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Link as MuiLink
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import GJMSConfig from "../../components/GJMSConfig"; // Import the new GJMSConfig
import useAuthCheck from "../../hooks/useAuthCheck"; // Assuming you have this hook
import { useOutletContext, useNavigate } from "react-router-dom";
//import { database } from '../../firebase'; // Fixed import path for firebase

// Define tab configurations outside the component to avoid unnecessary re-renders.
const tabConfigurations = [
    {
        label: "GeoJSON Lokasi",
        dbPath: "db_peta/tb_geojson/tb_lokasi",
        fieldName: "lokasi", // Field to display as the main name
        primaryKeyField: "firebaseKey", // This will be the Firebase key
    },
    {
        label: "GeoJSON Jalan Utama",
        dbPath: "db_peta/tb_geojson/tb_jalan_utama",
        fieldName: "Id", // Field to display as the main name
        primaryKeyField: "firebaseKey", // This will be the Firebase key
    },
    {
        label: "GeoJSON Lebung Area",
        dbPath: "db_peta/tb_geojson/tb_lebung_area",
        fieldName: "Id", // Field to display as the main name
        primaryKeyField: "firebaseKey", // This will be the Firebase key
    },
    {
        label: "GeoJSON Pipa Gas",
        dbPath: "db_peta/tb_geojson/tb_pipa_gas",
        fieldName: "Id", // Field to display as the main name
        primaryKeyField: "firebaseKey", // This will be the Firebase key
    },
    {
        label: "GeoJSON Pos Keamanan",
        dbPath: "db_peta/tb_geojson/tb_pos_keamanan",
        fieldName: "Id", // Field to display as the main name
        primaryKeyField: "firebaseKey", // This will be the Firebase key
    },
];

function GJMS() {
    const { user, loading } = useAuthCheck();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();

    const { breadcrumbs } = useOutletContext();

    const [tabIndex, setTabIndex] = useState(() => {
        // Retrieve stored tab index from localStorage, default to 0
        const storedTabIndex = localStorage.getItem('gjmsTabIndex');
        const parsedIndex = parseInt(storedTabIndex, 10);
        if (!isNaN(parsedIndex) && parsedIndex >= 0 && parsedIndex < tabConfigurations.length) {
            return parsedIndex;
        }
        return 0; // Default to the first tab
    });

    // Save tab index to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('gjmsTabIndex', tabIndex.toString());
    }, [tabIndex]);

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const currentTabConfig = tabConfigurations[tabIndex];

    return (
        <Box sx={{ pb: isMobile ? 8 : 0 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                {breadcrumbs && breadcrumbs.map((crumb, index) => (
                    crumb.path ? (
                        <MuiLink
                            key={index}
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate(crumb.path)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {crumb.label}
                        </MuiLink>
                    ) : (
                        <Typography key={index} color="text.primary">
                            {crumb.label}
                        </Typography>
                    )
                ))}
            </Breadcrumbs>

            {/* Layout */}
            <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 4 }}>
                {/* Main content */}
                <Box sx={{ flex: 1 }}>
                    <Card>
                        <CardContent>
                            {/* Render GJMSConfig dynamically based on tabIndex */}
                            {currentTabConfig && (
                                <GJMSConfig
                                    title={`Daftar ${currentTabConfig.label}`}
                                    dbPath={currentTabConfig.dbPath}
                                    fieldName={currentTabConfig.fieldName}
                                    primaryKeyField={currentTabConfig.primaryKeyField}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {/* Desktop sidebar */}
                {!isMobile && (
                    <Paper
                        elevation={1}
                        sx={{
                            alignSelf: "flex-start",
                        }}
                    >
                        <Tabs
                            orientation="vertical"
                            value={tabIndex}
                            onChange={handleTabChange}
                            TabIndicatorProps={{
                                sx: {
                                    left: 0,
                                    right: "auto",
                                    width: "4px",
                                    bgcolor: "primary.main",
                                },
                            }}
                            sx={{
                                width: 350,
                                borderLeft: 1,
                                borderColor: "divider",
                                ".MuiTab-root": {
                                    alignItems: "flex-start",
                                    justifyContent: "flex-start",
                                    px: 2,
                                },
                            }}
                        >
                            {/* Loop through tab configurations for sidebar */}
                            {tabConfigurations.map((tab, index) => (
                                <Tab key={index} label={tab.label} />
                            ))}
                        </Tabs>
                    </Paper>
                )}
            </Box>

            {/* Bottom Tabs (Mobile) */}
            {isMobile && (
                <Paper
                    sx={{
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        borderTop: 1,
                        borderColor: "divider",
                        zIndex: 1000,
                    }}
                    elevation={3}
                >
                    <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons
                        allowScrollButtonsMobile
                        TabIndicatorProps={{
                            sx: { bgcolor: "primary.main", height: 3 },
                        }}
                        centered={false}
                    >
                        {/* Loop through tab configurations for mobile */}
                        {tabConfigurations.map((tab, index) => (
                            <Tab key={index} label={tab.label} />
                        ))}
                    </Tabs>
                </Paper>
            )}
        </Box>
    );
}

export default GJMS;
